import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';
import { templatesApi, Template } from '@/lib/api/services/templates';
import {
  ShopifyPlaybookKey,
  ShopifyPlaybookStatusEntry,
  shopifyPlaybooksApi,
} from '@/lib/api/services/shopifyPlaybooks';
import { toast } from '@/hooks/use-toast';
import {
  detectTemplatePlaceholders,
  filterSuggestions,
  getVariableSuggestions,
  groupSuggestions,
  TemplatePlaceholder,
  VariableSuggestion,
} from './playbookPlaceholders';
import { PlaybookPreview } from './PlaybookPreview';

/**
 * Extract the plain-body text from a Meta template's components array.
 * Exported for unit tests — covered in shopifyPlaybookHelpers.test.ts.
 */
export function extractTemplateBody(
  template: Pick<Template, 'meta_response' | 'metaResponse'> | null | undefined
): string {
  if (!template) return '';
  const meta: any = (template as any).meta_response ?? (template as any).metaResponse ?? {};
  const components: any[] = Array.isArray(meta?.components)
    ? meta.components
    : Array.isArray(meta?.data?.components)
      ? meta.data.components
      : [];
  const body = components.find((c) => String(c?.type || '').toUpperCase() === 'BODY');
  return typeof body?.text === 'string' ? body.text : '';
}

function extractTemplateComponents(template: Template | null | undefined): any[] {
  if (!template) return [];
  const meta: any = (template as any).meta_response ?? (template as any).metaResponse ?? {};
  if (Array.isArray(meta?.components)) return meta.components;
  if (Array.isArray(meta?.data?.components)) return meta.data.components;
  return [];
}

interface PlaybookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playbook: ShopifyPlaybookStatusEntry | null;
  businessAccountId: string;
  onSaved: () => void;
}

export const PlaybookDialog: React.FC<PlaybookDialogProps> = ({
  open,
  onOpenChange,
  playbook,
  businessAccountId,
  onSaved,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * Which variable input is the "insert target" for the shared chip panel.
   * Set onFocus and left sticky across chip clicks (chip's onMouseDown
   * prevents blur), so the user can click several chips in a row without
   * re-focusing between each.
   */
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const isEdit = !!playbook?.flowId;

  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId(playbook?.templateId || '');
    setVariables({});
  }, [open, playbook?.templateId]);

  // On edit, pull the persisted variables so the form pre-fills.
  useEffect(() => {
    if (!open || !playbook?.flowId || !businessAccountId) return;
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      const res = await shopifyPlaybooksApi.getDetail(
        playbook.key as ShopifyPlaybookKey,
        businessAccountId
      );
      if (cancelled) return;
      if (!(res as any)?.error && (res as any)?.data?.variables) {
        setVariables({ ...(res as any).data.variables });
      }
      setDetailLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, playbook?.flowId, playbook?.key, businessAccountId]);

  useEffect(() => {
    if (!open || !businessAccountId) return;
    let cancelled = false;
    setTemplatesLoading(true);
    (async () => {
      const res = await templatesApi.getAll(businessAccountId);
      if (cancelled) return;
      if ((res as any)?.error) {
        toast({
          title: 'Could not load templates',
          description: (res as any).error,
          variant: 'destructive',
        });
        setTemplates([]);
      } else {
        const raw = Array.isArray((res as any)?.data) ? (res as any).data : [];
        const approved = raw.filter(
          (t: Template) => String(t.status || '').toUpperCase() === 'APPROVED'
        );
        setTemplates(approved);
      }
      setTemplatesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, businessAccountId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );
  const components = useMemo(
    () => extractTemplateComponents(selectedTemplate),
    [selectedTemplate]
  );
  const placeholders: TemplatePlaceholder[] = useMemo(
    () => detectTemplatePlaceholders(components),
    [components]
  );
  const suggestions = useMemo(
    () => (playbook ? getVariableSuggestions(playbook.triggerType) : []),
    [playbook?.triggerType]
  );

  // Free-text search over the variable library. Label OR raw expression match.
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const filteredSuggestions = useMemo(
    () => filterSuggestions(suggestions, suggestionQuery),
    [suggestions, suggestionQuery]
  );
  const suggestionGroups = useMemo(
    () => groupSuggestions(filteredSuggestions),
    [filteredSuggestions]
  );

  // Collapsed state per section. Default: only the "trigger" section is open
  // if present — contact + system are one-click away but out of the way. When
  // the user is actively searching, every matching section auto-opens so they
  // don't need to click through to see results.
  const [collapsedSections, setCollapsedSections] = useState<
    Record<VariableSuggestion['category'], boolean>
  >({ trigger: false, contact: true, system: true });
  const searching = suggestionQuery.trim().length > 0;

  function toggleSection(category: VariableSuggestion['category']) {
    setCollapsedSections((prev) => ({ ...prev, [category]: !prev[category] }));
  }

  // Reset search + collapse preferences when the playbook changes.
  useEffect(() => {
    setSuggestionQuery('');
    setCollapsedSections({ trigger: false, contact: true, system: true });
  }, [playbook?.key]);

  // Drop variable keys that no longer exist in the current template (swap case).
  useEffect(() => {
    if (placeholders.length === 0) return;
    const allowed = new Set(placeholders.map((p) => p.key));
    setVariables((prev) => {
      const next: Record<string, string> = {};
      let dropped = false;
      for (const [k, v] of Object.entries(prev)) {
        if (allowed.has(k)) next[k] = v;
        else dropped = true;
      }
      return dropped ? next : prev;
    });
  }, [placeholders]);

  const allFilled = placeholders.every((p) => (variables[p.key] ?? '').trim().length > 0);

  function updateVariable(key: string, value: string) {
    setVariables((prev) => ({ ...prev, [key]: value }));
  }

  // When the template changes (or first opens), focus the first empty
  // variable so the shared chip panel knows where to insert.
  useEffect(() => {
    if (!selectedTemplateId || placeholders.length === 0) {
      setFocusedKey(null);
      return;
    }
    const firstEmpty = placeholders.find((p) => !(variables[p.key] ?? '').trim());
    const target = firstEmpty ? firstEmpty.key : placeholders[0].key;
    setFocusedKey(target);
    // Defer the actual DOM focus to the next tick so the inputs have mounted.
    queueMicrotask(() => {
      inputRefs.current[target]?.focus();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, placeholders.length]);

  function handleChipClick(value: string) {
    const key = focusedKey;
    if (!key) return;
    updateVariable(key, value);
    // Keep DOM focus on the same input so users can click multiple chips
    // in a row without re-clicking the field.
    queueMicrotask(() => {
      inputRefs.current[key]?.focus();
    });
  }

  const focusedLabel =
    focusedKey && placeholders.find((p) => p.key === focusedKey)?.label;

  async function handleSave() {
    if (!playbook || !selectedTemplateId || !businessAccountId) return;
    setSaving(true);
    try {
      const payload = {
        templateId: selectedTemplateId,
        variables,
        businessAccountId,
      };
      const res = isEdit
        ? await shopifyPlaybooksApi.update(playbook.key as ShopifyPlaybookKey, payload)
        : await shopifyPlaybooksApi.enable(playbook.key as ShopifyPlaybookKey, payload);

      if ((res as any)?.error || (res as any)?.success === false) {
        toast({
          title: isEdit ? 'Could not update auto message' : 'Could not enable auto message',
          description: (res as any).error || 'Unknown error',
          variant: 'destructive',
        });
        return;
      }

      toast({ variant: "success",
        title: isEdit ? 'Auto message updated' : 'Auto message enabled',
        description: `${playbook.title} will now run on every "${playbook.triggerType}" event.`,
      });
      onSaved();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden p-0 flex flex-col"
        data-testid="shopify-playbook-dialog"
      >
        {/* Fixed header */}
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-lg">
            {isEdit ? 'Edit auto message' : 'Enable auto message'} · {playbook?.title}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {playbook?.description}{' '}
            Fires on <code className="text-[11px] bg-muted px-1 rounded">{playbook?.triggerType}</code>.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable 2-column body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-6 p-6">
            {/* LEFT — form */}
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="playbook-template-select" className="text-sm font-semibold">
                  Approved template
                </Label>
                <Select
                  value={selectedTemplateId}
                  onValueChange={setSelectedTemplateId}
                  disabled={templatesLoading || saving || detailLoading}
                >
                  <SelectTrigger
                    id="playbook-template-select"
                    data-testid="shopify-playbook-template-select"
                  >
                    <SelectValue
                      placeholder={
                        templatesLoading
                          ? 'Loading templates…'
                          : templates.length === 0
                            ? 'No approved templates — approve one first'
                            : 'Pick a template'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem
                        key={t.id}
                        value={t.id}
                        data-testid={`shopify-playbook-template-option-${t.name}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{t.name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {t.language}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {t.category}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only WhatsApp templates approved by Meta can be used. Pending / rejected are hidden.
                </p>
              </div>

              {selectedTemplate && placeholders.length > 0 && (
                <div className="space-y-3" data-testid="shopify-playbook-variables">
                  {/* Shared chip panel — inserts into whichever variable is focused.
                      Chips come from the same catalog the flow-builder uses
                      (web/src/components/automation/variableUtils.ts), grouped
                      by section so users aren't overwhelmed. */}
                  {suggestions.length > 0 && (
                    <div
                      className="rounded-md border border-[#95BF47]/30 bg-[#95BF47]/5 p-3 space-y-3"
                      data-testid="shopify-playbook-suggestion-panel"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-xs font-semibold text-gray-700">
                            Variable library
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            Same options as the WhatsApp flow builder.
                          </div>
                        </div>
                        <div
                          className="text-[11px] text-muted-foreground truncate max-w-[200px]"
                          data-testid="shopify-playbook-insert-target"
                        >
                          {focusedLabel ? (
                            <>
                              Inserting into{' '}
                              <span className="font-medium text-gray-700">{focusedLabel}</span>
                            </>
                          ) : (
                            'Click a field below first'
                          )}
                        </div>
                      </div>
                      {/* Search — filters across all groups by label or expression */}
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                        <Input
                          value={suggestionQuery}
                          onChange={(e) => setSuggestionQuery(e.target.value)}
                          placeholder="Search variables (e.g. order, first, tracking)…"
                          className="h-8 pl-7 pr-7 text-xs bg-white"
                          data-testid="shopify-playbook-suggest-search"
                        />
                        {suggestionQuery && (
                          <button
                            type="button"
                            onClick={() => setSuggestionQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            title="Clear"
                            data-testid="shopify-playbook-suggest-clear"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>

                      {suggestionGroups.length === 0 && (
                        <div className="text-xs text-muted-foreground py-3 text-center">
                          No variables match "{suggestionQuery}".
                        </div>
                      )}

                      {suggestionGroups.map((group) => {
                        // Searching auto-opens every matching group so
                        // results aren't hidden behind a collapsed header.
                        const collapsed = searching ? false : collapsedSections[group.category];
                        return (
                          <div key={group.title} className="space-y-1">
                            <button
                              type="button"
                              onClick={() => toggleSection(group.category)}
                              onMouseDown={(e) => e.preventDefault()}
                              disabled={searching}
                              className="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 disabled:cursor-default py-0.5"
                              data-testid={`shopify-playbook-suggest-group-${group.category}`}
                            >
                              <span className="flex items-center gap-1.5">
                                <ChevronDown
                                  className={`h-3 w-3 transition-transform ${
                                    collapsed ? '-rotate-90' : ''
                                  }`}
                                />
                                {group.title}
                              </span>
                              <span className="font-normal normal-case text-muted-foreground">
                                {group.items.length}
                              </span>
                            </button>
                            {!collapsed && (
                              <div className="flex flex-wrap gap-1 pl-4">
                                {group.items.map((s) => (
                                  <button
                                    key={s.value}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleChipClick(s.value)}
                                    disabled={!focusedKey || saving}
                                    className="rounded-full border border-[#95BF47]/30 bg-white hover:bg-[#95BF47]/10 hover:border-[#95BF47]/60 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] px-2 py-0.5 transition-colors"
                                    title={`Insert ${s.value}`}
                                    data-testid={`shopify-playbook-suggest-${s.value}`}
                                  >
                                    {s.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-sm font-semibold">Fill in the variables</div>
                      <p className="text-xs text-muted-foreground">
                        Pick a field to set the insert target, then tap a chip above — or type
                        a static value.
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {Object.values(variables).filter((v) => (v || '').trim()).length}/
                      {placeholders.length} set
                    </Badge>
                  </div>

                  {placeholders.map((p) => {
                    const isActive = focusedKey === p.key;
                    const filled = (variables[p.key] ?? '').trim().length > 0;
                    return (
                      <div
                        key={p.key}
                        className={`rounded-md border p-3 space-y-2 transition-colors ${
                          isActive
                            ? 'border-[#95BF47] bg-[#95BF47]/5'
                            : 'border-border bg-muted/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <Label
                            htmlFor={`playbook-var-${p.key}`}
                            className="text-xs font-semibold"
                          >
                            {p.label}
                            {isActive && (
                              <span className="ml-2 text-[10px] font-normal text-[#5a7d2a]">
                                ← insert target
                              </span>
                            )}
                          </Label>
                          <span
                            className={`text-[10px] font-medium ${
                              filled ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                          >
                            {filled ? 'Set' : 'Required'}
                          </span>
                        </div>
                        <Input
                          id={`playbook-var-${p.key}`}
                          data-testid={`shopify-playbook-var-${p.key}`}
                          ref={(el) => {
                            inputRefs.current[p.key] = el;
                          }}
                          value={variables[p.key] ?? ''}
                          onChange={(e) => updateVariable(p.key, e.target.value)}
                          onFocus={() => setFocusedKey(p.key)}
                          placeholder={'e.g. {{first_name}} or "Paris"'}
                          className="font-mono text-xs bg-white"
                          disabled={saving}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedTemplate && placeholders.length === 0 && (
                <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                  This template has no variables — nothing to fill in. Click Enable to activate.
                </div>
              )}
            </div>

            {/* RIGHT — sticky live preview */}
            <div className="md:sticky md:top-0 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Template Preview</div>
                  <p className="text-xs text-muted-foreground">
                    Updates as you fill values in the form.
                  </p>
                </div>
              </div>

              {selectedTemplate ? (
                <PlaybookPreview components={components} variables={variables} />
              ) : (
                <div className="rounded-2xl bg-[#E6DDD4] p-8 text-center text-xs text-gray-600">
                  Pick a template on the left to see the preview.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed footer */}
        <DialogFooter className="p-4 border-t bg-background">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedTemplateId || saving || !allFilled}
            className="bg-[#95BF47] hover:bg-[#7a9d3a] text-white"
            data-testid="shopify-playbook-save"
            title={!allFilled ? 'Fill in every variable before saving' : undefined}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Save changes' : 'Enable auto message'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
